import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "./AuthContext";
import { notificationsApi, notificationsStreamUrl, notificationsStreamToken } from "../services/api";
import { shouldDisplayDesktop, showDesktopNotification } from "../services/desktopNotifications";

// Persistent Notification Center: initial REST load + real-time SSE push.
// Deliberately separate from NotificationContext (the ephemeral toast feed).
const NotificationCenterContext = createContext(null);

const isDemo = () => localStorage.getItem("demo_mode") === "true";
const MAX_ITEMS = 20;

export function NotificationCenterProvider({ children }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const esRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const [list, count] = await Promise.all([
        notificationsApi.list({ limit: MAX_ITEMS }),
        notificationsApi.unreadCount(),
      ]);
      setItems(Array.isArray(list) ? list : []);
      setUnreadCount(count?.unread_count ?? 0);
    } catch {
      /* keep last known state on transient errors */
    }
  }, []);

  useEffect(() => {
    if (!user || isDemo()) {
      setItems([]);
      setUnreadCount(0);
      return;
    }

    refresh();

    let es;
    let cancelled = false;
    let retryTimer = null;
    let attempt = 0;

    // The stream token is valid for 60 seconds and is part of the URL. When the connection drops (sleep, network blip,
    // redeploy) the browser retries the SAME url; the server now answers 401 and the browser closes the EventSource for
    // good (readyState CLOSED), so live notifications stopped until a page reload. A closed stream therefore gets a fresh
    // token and a new EventSource, with backoff, and a REST refresh for whatever was missed meanwhile.
    const scheduleReconnect = () => {
      if (cancelled || retryTimer) return;
      const delay = Math.min(60000, 2000 * 2 ** attempt);
      attempt += 1;
      retryTimer = setTimeout(async () => {
        retryTimer = null;
        if (cancelled) return;
        await refresh();
        if (!cancelled) connect();
      }, delay);
    };

    const connect = async () => {
      try {
        const streamToken = await notificationsStreamToken();
        if (cancelled) return;
        es = new EventSource(notificationsStreamUrl(streamToken));
        esRef.current = es;
        es.onopen = () => { attempt = 0; };
        es.onmessage = (e) => {
          if (!e.data) return;
          let payload;
          try { payload = JSON.parse(e.data); } catch { return; }
          if (payload.event !== "notification" || !payload.notification) return;

          const n = payload.notification;
          setItems((prev) => [n, ...prev.filter((x) => x.id !== n.id)].slice(0, MAX_ITEMS));
          if (typeof payload.unread_count === "number") setUnreadCount(payload.unread_count);
          else setUnreadCount((c) => c + 1);

          // A technician can be actively looking at the exact ticket a push notification is about
          // (customer reply, assignment, reopen/close, mention, ...): none of TicketDetail's own
          // per-ticket queries have a refetchInterval, so without this they'd sit stale until an
          // unrelated remount or window-focus refetch, even while the bell badge already updated.
          if (n.ticket_id) {
            qc.invalidateQueries({ queryKey: ["ticket", n.ticket_id] });
            qc.invalidateQueries({ queryKey: ["comments", n.ticket_id] });
            qc.invalidateQueries({ queryKey: ["history", n.ticket_id] });
            qc.invalidateQueries({ queryKey: ["email-thread", n.ticket_id] });
            qc.invalidateQueries({ queryKey: ["ticket-attachments", n.ticket_id] });
          }

          if (shouldDisplayDesktop(payload)) showDesktopNotification(n);
        };
        es.onerror = () => {
          // CONNECTING: the browser is retrying by itself. CLOSED (2): it gave up (expired token, 401): start over.
          if (cancelled || es.readyState !== 2) return;
          try { es.close(); } catch {}
          esRef.current = null;
          scheduleReconnect();
        };
      } catch {
        /* SSE unavailable (token request failed, offline): the badge still works via REST refresh; try again later */
        scheduleReconnect();
      }
    };

    connect();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      try { es && es.close(); } catch {}
      esRef.current = null;
    };
  }, [user, refresh, qc]);

  const markRead = useCallback(async (id) => {
    setItems((prev) => {
      const target = prev.find((n) => n.id === id);
      if (target && !target.is_read) setUnreadCount((c) => Math.max(0, c - 1));
      return prev.map((n) => (n.id === id ? { ...n, is_read: true } : n));
    });
    try { await notificationsApi.markRead(id); } catch { refresh(); }
  }, [refresh]);

  const markAllRead = useCallback(async () => {
    setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
    setUnreadCount(0);
    try { await notificationsApi.markAllRead(); } catch { refresh(); }
  }, [refresh]);

  return (
    <NotificationCenterContext.Provider value={{ items, unreadCount, markRead, markAllRead, refresh }}>
      {children}
    </NotificationCenterContext.Provider>
  );
}

export const useNotificationCenter = () =>
  useContext(NotificationCenterContext) ?? {
    items: [], unreadCount: 0, markRead: () => {}, markAllRead: () => {}, refresh: () => {},
  };
